import React, { useState } from 'react';
import { useImportQueue } from '../contexts/ImportQueueContext';
import { Loader2, FileDown, CheckCircle2, XCircle, AlertCircle, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';

export const ImportQueueWidget = () => {
    const { queue, isProcessing, showReport, closeReport, cancelImport } = useImportQueue();
    const [showErrors, setShowErrors] = useState(false);
    const [showDetails, setShowDetails] = useState(false);

    if (queue.length === 0 && !showReport) return null;

    const total = queue.length;
    const pending = queue.filter(q => q.status === 'pending').length;
    const processed = total - pending;

    const successes = queue.filter(q => q.status === 'success' && q.result?.status !== 'ALREADY_EXISTS');
    const duplicates = queue.filter(q => q.status === 'success' && q.result?.status === 'ALREADY_EXISTS');
    const errors = queue.filter(q => q.status === 'error');

    // Download CSV
    const downloadReport = () => {
        const rows = [
            ['Expediente', 'Estado', 'Detalle']
        ];
        queue.forEach(item => {
            let estado = 'Pendiente';
            let detalle = '';
            if (item.status === 'success') {
                estado = item.result?.status === 'ALREADY_EXISTS' ? 'Duplicada' : 'Importada';
                detalle = item.result?.message || '';
            } else if (item.status === 'error') {
                estado = 'Error';
                detalle = item.errorMsg || 'Error desconocido';
            }
            rows.push([item.expediente, estado, detalle]);
        });

        const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporte-importacion-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {/* INLINE WIDGET IN HEADER */}
            {isProcessing && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full cursor-pointer hover:bg-indigo-100 transition-colors"
                    onClick={() => setShowDetails(true)}
                >
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                    <span className="text-sm font-medium text-indigo-800">
                        Importando {processed} de {total}
                    </span>
                </div>
            )}

            {/* ONGOING DETAILS MODAL */}
            <Dialog open={showDetails} onOpenChange={setShowDetails}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-gray-900">
                            {isProcessing ? <Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> : <CheckCircle2 className="h-5 w-5 text-green-600" />}
                            Progreso de Importación
                        </DialogTitle>
                        <DialogDescription>
                            Detalle de los expedientes en cola y su estado actual.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-2 space-y-4">
                        <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
                            <div className="bg-indigo-600 h-2 rounded-full transition-all duration-500" style={{ width: `${total > 0 ? (processed / total) * 100 : 0}%` }}></div>
                        </div>

                        <div className="grid grid-cols-4 gap-2 text-center text-sm mb-4">
                            <div className="bg-gray-50 rounded p-2"><span className="font-bold">{total}</span><br />Total</div>
                            <div className="bg-indigo-50 rounded p-2"><span className="font-bold text-indigo-700">{pending}</span><br />Pendientes</div>
                            <div className="bg-green-50 rounded p-2"><span className="font-bold text-green-700">{successes.length}</span><br />Nuevas</div>
                            <div className="bg-red-50 rounded p-2"><span className="font-bold text-red-700">{errors.length}</span><br />Errores</div>
                        </div>

                        <div className="border rounded-lg overflow-hidden flex flex-col max-h-64">
                            <div className="bg-gray-50 border-b px-4 py-2 font-medium text-xs text-gray-500 flex items-center justify-between">
                                <span>Expediente</span>
                                <span>Estado</span>
                            </div>
                            <div className="overflow-y-auto flex-1 divide-y">
                                {queue.map((item, idx) => (
                                    <div key={idx} className="px-4 py-2 flex items-center justify-between text-sm">
                                        <span className="font-mono">{item.expediente}</span>
                                        <div className="flex items-center gap-2 text-xs">
                                            {item.status === 'processing' && <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Procesando</span>}
                                            {item.status === 'pending' && <span className="text-gray-500 px-2 py-0.5 rounded bg-gray-100">En cola</span>}
                                            {item.status === 'success' && item.result?.status !== 'ALREADY_EXISTS' && <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded">Creada</span>}
                                            {item.status === 'success' && item.result?.status === 'ALREADY_EXISTS' && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Duplicada</span>}
                                            {item.status === 'error' && <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded truncate max-w-[150px]" title={item.errorMsg}>Error: {item.errorMsg}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="flex items-center justify-between">
                        <div>
                            {pending > 0 && (
                                <Button variant="destructive" onClick={cancelImport} className="flex items-center gap-2 bg-red-100 text-red-700 hover:bg-red-200 border-none shadow-none">
                                    <XCircle className="h-4 w-4" /> Cancelar Importación
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {queue.length > 0 && !isProcessing && (
                                <Button variant="outline" onClick={downloadReport} className="flex items-center gap-2">
                                    <FileDown className="h-4 w-4" /> CSV
                                </Button>
                            )}
                            <Button onClick={() => setShowDetails(false)} className="bg-indigo-600 text-white hover:bg-indigo-700">
                                Cerrar
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* FINAL REPORT MODAL */}
            <Dialog open={showReport} onOpenChange={closeReport}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-gray-900">
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                            Reporte de Importación
                        </DialogTitle>
                        <DialogDescription>
                            El proceso de importación en bloque ha finalizado.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4 space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
                                <p className="text-2xl font-bold text-green-700">{successes.length}</p>
                                <p className="text-xs text-green-600 mt-1">Nuevas</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-100">
                                <p className="text-2xl font-bold text-amber-700">{duplicates.length}</p>
                                <p className="text-xs text-amber-600 mt-1">Ya existían</p>
                            </div>
                            <div className="bg-red-50 rounded-lg p-4 text-center border border-red-100">
                                <p className="text-2xl font-bold text-red-700">{errors.length}</p>
                                <p className="text-xs text-red-600 mt-1">Fallaron</p>
                            </div>
                        </div>

                        {errors.length > 0 && (
                            <div className="border border-red-200 rounded-lg overflow-hidden mt-4">
                                <button
                                    onClick={() => setShowErrors(p => !p)}
                                    className="w-full flex items-center justify-between px-4 py-2 bg-red-50 text-left text-sm font-medium text-red-800 hover:bg-red-100"
                                >
                                    <span className="flex items-center gap-2">
                                        <XCircle className="h-4 w-4" />
                                        Ver errores
                                    </span>
                                    {showErrors ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </button>
                                {showErrors && (
                                    <div className="max-h-48 overflow-y-auto divide-y divide-red-100">
                                        {errors.map((r, i) => (
                                            <div key={i} className="px-4 py-2 text-sm flex items-start gap-2">
                                                <span className="font-mono font-semibold text-red-700 shrink-0">{r.expediente}</span>
                                                <span className="text-gray-600 text-xs">{r.errorMsg}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={downloadReport} className="flex items-center gap-2">
                            <FileDown className="h-4 w-4" /> Descargar CSV
                        </Button>
                        <Button onClick={closeReport} className="bg-indigo-600 text-white">
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
